import { useState } from "react"

import {
  getCustomerApplicationsLatestSubmittedRetrieveQueryKey,
  useCustomerAppDocRetrieveSuspense,
  getCustomerApplicationsListQueryKey,
  getCustomerFundingNeedsListQueryKey,
  getCustomerLendersAvailableListQueryKey,
  useCustomerApplicationsRetrieveSuspense,
  useCustomerApplicationsSubmitUpdate,
  useCustomerCompanyRetrieveSuspense,
} from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"

import { ApplicationsDialog } from "@/components/applications/ApplicationsDialog"
import { useReconcilePendingApplicationDocument } from "@/components/applications/sign/useReconcilePendingApplicationDocument"
import { SummaryCard } from "@/components/summary/SummaryCard"
import { ApplicationFlowContext } from "@/lib/flows/application"

export const Route = createFileRoute(
  "/_dashboard/_application-flow/applications/$applicationUuid/submit",
)({
  component: RouteComponent,
  beforeLoad: ({ context: { store } }) => {
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/" })
    }
    return { companyUuid }
  },
})

function RouteComponent() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { applicationUuid } = Route.useParams()
  const { companyUuid } = Route.useRouteContext()
  const flowActorRef = ApplicationFlowContext.useActorRef()
  const [dialogOpen, setArticlesOfIncorporationDialogOpen] = useState(false)
  const { data: company, queryKey: companyDataQueryKey } =
    useCustomerCompanyRetrieveSuspense(companyUuid)
  const { data: application, queryKey: applicationQueryKey } =
    useCustomerApplicationsRetrieveSuspense(companyUuid, applicationUuid)
  const { data: applicationDocument } = useCustomerAppDocRetrieveSuspense(
    companyUuid,
    application.fundingNeeds.uuid,
  )

  useReconcilePendingApplicationDocument({
    companyUuid,
    applicationUuid,
    fundingNeedsUuid: application.fundingNeeds.uuid,
    isSigned: application.fundingNeeds.isSigned,
    lifecycle: applicationDocument.lifecycle,
    signerEvidenceInvalid: applicationDocument.signerEvidenceInvalid,
  })

  const { mutate: submit, isPending } = useCustomerApplicationsSubmitUpdate({
    mutation: {
      onSuccess: async (application) => {
        queryClient.setQueryData(applicationQueryKey, application)
        queryClient.invalidateQueries({
          queryKey: getCustomerApplicationsListQueryKey(companyUuid),
        })
        queryClient.invalidateQueries({
          queryKey: getCustomerLendersAvailableListQueryKey(companyUuid),
        })
        queryClient.invalidateQueries({
          queryKey: getCustomerFundingNeedsListQueryKey(companyUuid),
        })
        queryClient.invalidateQueries({
          queryKey:
            getCustomerApplicationsLatestSubmittedRetrieveQueryKey(companyUuid),
        })
        toast.success("Application submitted successfully.")

        if (application.easyApplyUrl) {
          window.open(application.easyApplyUrl, "_blank")
        }

        await navigate({
          to: "/funding-requests",
        })
      },
      onError: (error) => {
        toast.error("Error when submitting application.", {
          description: error.message,
        })
      },
    },
  })

  const handleBack = async () => {
    flowActorRef.send({ type: "back" })
  }

  const handleSubmit = () => {
    if (!company.hasAllRequiredDocuments) {
      setArticlesOfIncorporationDialogOpen(true)
      return
    }

    if (application.isReadyToSubmit)
      submit({
        companyUuid: companyUuid,
        uuid: application.uuid,
      })
  }

  return (
    <div>
      <SummaryCard
        companyUuid={companyUuid}
        application={application}
        handleBack={handleBack}
        handleSubmit={handleSubmit}
        isLoading={isPending}
        returnTo={`/applications/${applicationUuid}/submit`}
      />
      <ApplicationsDialog
        companyUuid={companyUuid}
        onNext={async () => {
          await queryClient.invalidateQueries({ queryKey: companyDataQueryKey })
          await queryClient.invalidateQueries({ queryKey: applicationQueryKey })
          setArticlesOfIncorporationDialogOpen(false)
        }}
        dialogOpen={dialogOpen}
      />
    </div>
  )
}
